import { UTCDate } from '@date-fns/utc';
import { ColumnDef } from '@tanstack/react-table';
import { format, getTime, isSameYear } from 'date-fns';
import { AmountDisplay } from '../../../../design-system/amount-display/amount-display';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Stack } from '../../../../design-system/stack/stack';
import { TransactionView } from '../../../../types/entity-types';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';
import { TransactionFilter } from './recent-transactions-table';

export const recentTransactionsColumnsDefinition: ColumnDef<TransactionView>[] = [
  {
    id: 'transactionType',
    accessorFn: (row: TransactionView) => (row.isCredit ? 'money_in' : 'money_out'),
    enableHiding: true,
    enableSorting: false,
    enableGlobalFilter: false,
    filterFn: (row, _columnId, filterValue: TransactionFilter) => {
      if (filterValue === 'all') return true;
      if (filterValue === 'money_in') return row.original.isCredit;
      if (filterValue === 'money_out') return !row.original.isCredit;
      return true;
    },
  },
  {
    id: 'fspDate',
    accessorFn: (row) => getTime(row.fspDate),
    header: 'Date',
    cell: ({ row }) => {
      const transactionDate = row.original.fspDate;
      const currentYear = new UTCDate();
      const dateFormat = isSameYear(transactionDate, currentYear) ? 'dd MMM' : 'dd MMM yyyy';

      return <p className="text-sm text-black">{format(transactionDate, dateFormat)}</p>;
    },
    enableGlobalFilter: false,
    enableSorting: false,
    size: 10,
    minSize: 10,
  },
  {
    id: 'vendor',
    header: 'Vendor',
    accessorFn: ({ vendor }) => vendor?.name,
    cell: ({ row }) => {
      const vendor = row.original.vendor;
      const payee = row.original.payee;
      const beneficiary = row.original.beneficiary;

      let name = 'Unknown';
      let logo: string | undefined;

      if (vendor?.name && vendor.logo) {
        name = vendor.name;
        logo = vendor.logo;
      } else if (payee?.name) {
        name = payee.name;
      } else if (beneficiary?.name) {
        name = beneficiary.name;
      }

      return <AvatarNameDescription avatar={{ showAvatar: true, avatarUrl: logo, size: 'xs' }} name={name} />;
    },
    enableSorting: false,
    size: 30,
  },
  {
    accessorKey: 'category',
    header: 'Category',
    accessorFn: ({ vendor }) => vendor?.category?.level2,
    cell: ({ row }) => {
      // AI category enrichment for a transaction is completed when the transaction vendor is linked to a category
      const categoryEnrichmentCompleted = !!row.original.vendor?.categoryId;

      const categoryDisplayName = categoryEnrichmentCompleted
        ? (row.original.vendor?.category?.level2 ?? 'Uncategorized')
        : 'Fetching category details...';

      return (
        <AvatarNameDescription
          avatar={
            row.original.vendor?.category?.level2
              ? {
                  showAvatar: true,
                  avatarUrl: row.original.vendor?.category?.icon,
                  size: 'xs',
                  faIcon: {
                    isFaIcon: true,
                    faIconName: row.original.vendor?.category?.icon || '',
                  },
                }
              : // If no category, don't show avatar to avoid empty space
                { showAvatar: false }
          }
          name={categoryDisplayName}
        />
      );
    },
    enableSorting: false,
    size: 25,
  },
  {
    id: 'account',
    header: 'Account',
    cell: ({ row }) => {
      const { name, logo } = {
        name: row.original.account.institution?.name,
        logo: row.original.account.institution?.logo,
      };

      const accountNameWithCurrency = `${name ?? 'Unknown'} (${row.original.account.currency})`;

      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl: logo, size: 'xs' }}
          name={accountNameWithCurrency ?? ''}
        />
      );
    },
    enableGlobalFilter: false,
    enableSorting: false,
    size: 25,
  },
  {
    id: 'amount',
    header: 'Amount',
    accessorFn: (row) => {
      return getNormalizedAmount({ amount: row.amount, isCredit: row.isCredit });
    },
    cell: ({ row }) => {
      const normalizedAmount = getNormalizedAmount({ amount: row.original.amount, isCredit: row.original.isCredit });
      const normalizedBaseCurrencyAmount = getNormalizedAmount({
        amount: row.original.baseCurrencyAmount,
        isCredit: row.original.isCredit,
      });
      const shouldDisplayBaseCurrencyAmount = row.original.currency !== row.original.baseCurrency;

      return (
        <Stack align="end" space={2}>
          <AmountDisplay normalizedAmount={normalizedAmount} currency={row.original.currency} />

          {shouldDisplayBaseCurrencyAmount && (
            <AmountDisplay
              normalizedAmount={normalizedBaseCurrencyAmount}
              currency={row.original.baseCurrency}
              isSecondary
            />
          )}
        </Stack>
      );
    },
    size: 20,
  },
];
